import { TCreateOrganizationReqDto } from "@/api/services/admin/organization/dtos/create-organization.dto";
import { useGetProvinceAndMunicipalityList } from "@/api/services/global/constant";
import { IconOpenInNew } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { SectionCard } from "@/components/elements/SectionCard";
import { Select } from "@/components/elements/Select";
import { black, text } from "@/theme/colors";
import { Grid, Stack, Typography } from "@mui/material";
import { FC } from "react";
import { Control, UseFormGetValues, UseFormSetValue, useWatch } from "react-hook-form";

type Props = {
  control: Control<TCreateOrganizationReqDto>;
  getValues: UseFormGetValues<TCreateOrganizationReqDto>;
  setValue: UseFormSetValue<TCreateOrganizationReqDto>;
};

export const OrganizationCreateBasicInfo: FC<Props> = ({ control, getValues, setValue }) => {
  const { isLoading: isLoadingProvincesAndMunicipalities, provinceOptions, getMunicipalityOptionsByProvince } = useGetProvinceAndMunicipalityList();

  const provinceWatchField = useWatch({ control, name: "province" });

  const handleOpenCRM = () => {
    const name = getValues("name");
    const url = `https://crm.zoho.jp/crm/irohana/search?isRelevance=false&modules=Accounts&searchword=${encodeURIComponent(name)}`;
    window.open(url, "_blank");
  };

  const handleOpenSecondCRM = () => {
    const url = `https://crm.zoho.jp/crm/irohana/tab/Accounts/create`;
    window.open(url, "_blank");
  };

  return (
    <SectionCard title="基本情報" titleVariant="head20Bold" maxWidth="100%" titleColor={black[30]}>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Stack>
            <FormField label="組織ID（自動生成）">
              <Input nonControl readonly value="(保存後に自動生成されます)" clearable={false} />
            </FormField>
          </Stack>
        </Grid>
        <Grid item xs={6}>
          <FormField label="ステータス" required>
            <Input nonControl readonly value="新規登録" clearable={false} />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="name">
            <Input />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="type">
            <Select enumName="EOrganizationType" />
          </FormField>
        </Grid>

        <Grid item xs={4}>
          <FormField control={control} name="province">
            <Select
              onChange={() => {
                setValue("municipality", "");
              }}
              options={provinceOptions}
              loading={isLoadingProvincesAndMunicipalities}
            />
          </FormField>
        </Grid>
        <Grid item xs={4}>
          <FormField control={control} name="municipality">
            <Select
              readonly={!provinceWatchField}
              options={provinceWatchField ? getMunicipalityOptionsByProvince(provinceWatchField) : []}
              loading={isLoadingProvincesAndMunicipalities}
            />
          </FormField>
        </Grid>
        <Grid item xs={4}>
          <FormField label="担当者">
            <Input nonControl readonly value="(保存後に設定可能)" clearable={false} />
          </FormField>
        </Grid>

        <Grid item xs={12}>
          <FormField control={control} name="crmLink">
            <Stack
              direction="row"
              alignItems="center"
              gap="24px"
              sx={{
                ".MuiTextField-root": {
                  flex: 1,
                },
              }}
            >
              <Input />
              <Stack gap="12px" flexDirection="row">
                <Button
                  onClick={handleOpenCRM}
                  variant="ghost"
                  size="sm"
                  sx={{
                    cursor: "pointer",
                    height: "33px !important",
                  }}
                >
                  <Typography variant="body14Semi" color={text.secondary} pr="8px">
                    CRM会社検索
                  </Typography>
                  <IconOpenInNew />
                </Button>
                <Button
                  onClick={handleOpenSecondCRM}
                  variant="ghost"
                  size="sm"
                  sx={{
                    cursor: "pointer",
                    height: "33px !important",
                  }}
                >
                  <Typography variant="body14Semi" color={text.secondary} pr="8px">
                    CRM会社登録
                  </Typography>
                  <IconOpenInNew />
                </Button>
              </Stack>
            </Stack>
          </FormField>
        </Grid>
      </Grid>
    </SectionCard>
  );
};
